import { SUPPORT_WRITE_PERMISSIONS, permissionAction } from './permissions.js';
import { PARTNER_ROLE_KEYS, PARTNER_SETUP_ROLE_KEYS, permissionsForDeviceRole, permissionsForPartnerRole, permissionsForRole, } from './roles.js';
/**
 * Thrown by `assertCan`. **Deliberately not an HTTP error** — authz is
 * isomorphic and carries no status codes. `apps/api`'s error middleware maps
 * this to a 403; the SPA catches it to hide an action. Keeping the mapping at
 * the edge is what lets the same check run in the browser.
 */
export class AuthorizationDeniedError extends Error {
    permission;
    constructor(permission) {
        super(`Authorization denied: ${permission}`);
        this.name = 'AuthorizationDeniedError';
        this.permission = permission;
    }
}
/**
 * `PARTNER_SETUP_ROLES` keys only. `permissionsForPartnerRole` also answers for `PARTNER_ROLES`,
 * which a setup session never holds, so a key that reaches here at runtime outside the type —
 * e.g. `'partner.owner'` or `'tenant.owner'` — must grant nothing.
 */
const PARTNER_SETUP_PRINCIPAL_ROLE_KEYS = new Set(PARTNER_SETUP_ROLE_KEYS);
/**
 * Step 0: a setup session (adr/0035 decisions 4 and 5) — terminal, tenant-wide, and resolved
 * through `PARTNER_SETUP_ROLES` only. Operator, device, partner and assignment authority on the
 * same context is never consulted.
 */
function grantedBySetupRole(session, permission) {
    if (!PARTNER_SETUP_PRINCIPAL_ROLE_KEYS.has(session.roleKey))
        return false;
    return permissionsForPartnerRole(session.roleKey).has(permission);
}
/**
 * Step 1 of resolution: the platform-operator axis (docs/005 §"Platform
 * operators"). `SUPERADMIN` grants everything; `SUPPORT` is read-only
 * cross-tenant, so it grants only permissions whose action segment is `read`, plus the explicit
 * allow-list `SUPPORT_WRITE_PERMISSIONS`.
 *
 * An operator with a `null` platformRole grants nothing here and falls through
 * to ordinary tenant resolution — inconsistent data must fail closed.
 *
 * **Auditing is not authz's job.** docs/005 requires every cross-tenant read to
 * write an `AuditEvent`; that happens in the API's cross-tenant access function
 * and the audit module (S9). Putting it here would make `can()` do I/O and stop
 * it running in the browser.
 */
function grantedByPlatformOperator(ctx, permission) {
    if (!ctx.isPlatformOperator)
        return false;
    if (ctx.platformRole === 'SUPERADMIN')
        return true;
    if (ctx.platformRole === 'SUPPORT') {
        return permissionAction(permission) === 'read' || SUPPORT_WRITE_PERMISSIONS.has(permission);
    }
    return false;
}
/**
 * Does the caller care about a specific location? `undefined` (no target) and an
 * explicit `null` both mean "no particular location".
 */
function targetLocationId(target) {
    return target?.locationId ?? null;
}
/**
 * Step 2: a paired device is a principal, not an anonymous caller. Its role
 * comes from `appId` and is LOCATION-scoped to the device's own `locationId`,
 * both of which come from the `Device` row and never from the request.
 */
function grantedByDeviceRole(ctx, permission, target) {
    if (ctx.appId === null)
        return false;
    if (!permissionsForDeviceRole(ctx.appId).has(permission))
        return false;
    const wanted = targetLocationId(target);
    return wanted === null || wanted === ctx.locationId;
}
/**
 * `PARTNER_ROLES` keys only. `permissionsForPartnerRole` also answers for the setup roles
 * (`PARTNER_SETUP_ROLES`), which a partner principal never holds (adr/0035 decision 5), so a
 * key that reaches here at runtime outside the type — e.g. `'partner.setup'` — must grant nothing.
 */
const PARTNER_PRINCIPAL_ROLE_KEYS = new Set(PARTNER_ROLE_KEYS);
/**
 * Step 3: a partner principal (adr/0035 decision 2) — terminal, like a device. Resolves
 * through `PARTNER_ROLES` only: its `assignments` are never consulted, so a partner principal
 * can never satisfy a tenant permission, and a tenant principal (`partnerRole === null`) never
 * reaches this branch.
 */
function grantedByPartnerRole(ctx, permission) {
    if (ctx.partnerRole === null)
        return false;
    if (!PARTNER_PRINCIPAL_ROLE_KEYS.has(ctx.partnerRole))
        return false;
    return permissionsForPartnerRole(ctx.partnerRole).has(permission);
}
/**
 * Step 4: an assignment grants when its role contains the permission and its
 * scope matches. `TENANT` matches anything; `LOCATION` matches only its own
 * `scopeId`.
 *
 * **Subtle, on purpose:** when no target is supplied a `LOCATION` assignment
 * still grants. The question `can(ctx, 'visitor.visit.read')` is "may this user
 * reach the read route at all", and a location-scoped user may — they just see
 * fewer rows. Narrowing *which* rows is `allowedLocationIds()`'s job and it must
 * reach the SQL `WHERE` (docs/005 §"List scoping"). Denying here instead would
 * 403 every location-scoped user off every list screen.
 */
function assignmentGrants(assignment, permission, target) {
    if (!permissionsForRole(assignment.roleKey).has(permission))
        return false;
    if (assignment.scopeType === 'TENANT')
        return true;
    const wanted = targetLocationId(target);
    if (wanted === null)
        return true;
    return assignment.scopeId !== null && assignment.scopeId === wanted;
}
/**
 * Resolution order (docs/005 "Resolution, in order"): setup session (terminal) → platform
 * operator → device principal → partner principal → matching assignment → deny.
 */
export function can(ctx, permission, target) {
    // Checked FIRST: a setup session never inherits operator, device, partner or assignment authority.
    if (ctx.setupSession !== undefined)
        return grantedBySetupRole(ctx.setupSession, permission);
    if (grantedByPlatformOperator(ctx, permission))
        return true;
    // A device principal has no `RoleAssignment` rows by construction, so this is
    // terminal rather than a fall-through.
    if (ctx.appId !== null)
        return grantedByDeviceRole(ctx, permission, target);
    // A partner principal is terminal too (adr/0035): its assignments are never consulted.
    if (ctx.partnerRole !== null)
        return grantedByPartnerRole(ctx, permission);
    return ctx.assignments.some((assignment) => assignmentGrants(assignment, permission, target));
}
/** `can`, as a guard. Throws `AuthorizationDeniedError`, which `apps/api` maps to 403. */
export function assertCan(ctx, permission, target) {
    if (!can(ctx, permission, target))
        throw new AuthorizationDeniedError(permission);
}
/**
 * The location filter for a list query — docs/005 §"List scoping".
 *
 * **`null` means ALL locations. `[]` means NONE.** Conflating them is a
 * cross-location data leak in one direction and a permanently empty screen in
 * the other. `{ in: [] }` correctly returns nothing — never translate an empty
 * array into an omitted `where` clause, and never translate `null` into a list.
 * The filter must reach the SQL `WHERE`: filtering after fetch breaks pagination
 * and counts, which is how you get "showing 20 of 400" rendering three rows.
 */
export function allowedLocationIds(ctx, permission) {
    // Tenant-wide or nothing: a setup role has no location scope.
    if (ctx.setupSession !== undefined)
        return grantedBySetupRole(ctx.setupSession, permission) ? null : [];
    // Platform operators are cross-tenant; location is not a boundary for them.
    if (grantedByPlatformOperator(ctx, permission))
        return null;
    // A device sees its own location and nothing else — never all locations.
    if (ctx.appId !== null) {
        if (!permissionsForDeviceRole(ctx.appId).has(permission))
            return [];
        return ctx.locationId === null ? [] : [ctx.locationId];
    }
    // A partner principal holds no location-scoped permission and never a tenant: NONE, fail closed.
    if (ctx.partnerRole !== null)
        return [];
    const locationIds = new Set();
    for (const assignment of ctx.assignments) {
        if (!permissionsForRole(assignment.roleKey).has(permission))
            continue;
        // A TENANT-scoped grant outranks every LOCATION-scoped one: all locations.
        if (assignment.scopeType === 'TENANT')
            return null;
        if (assignment.scopeId !== null)
            locationIds.add(assignment.scopeId);
    }
    return [...locationIds];
}
/**
 * `can`, for a route any ONE of several permissions unlocks — e.g. a location's kiosk
 * override, writable with `platform.location.manage` OR `visitor.settings.manage`.
 */
export function canAny(ctx, permissions, target) {
    return permissions.some((permission) => can(ctx, permission, target));
}
/**
 * `allowedLocationIds`, for a route any ONE of several permissions unlocks: the UNION of
 * each permission's location set. Same contract — **`null` means ALL locations, `[]`
 * means NONE** — so any permission granting all locations makes the union all locations.
 * Each permission is narrowed on its own before the union, so a location-scoped
 * `visitor.settings.manage` grant never widens to a location only another permission covers.
 */
export function allowedLocationIdsForAny(ctx, permissions) {
    const union = new Set();
    for (const permission of permissions) {
        const ids = allowedLocationIds(ctx, permission);
        if (ids === null)
            return null;
        for (const id of ids)
            union.add(id);
    }
    return [...union];
}
//# sourceMappingURL=can.js.map